/**
 * @file 一个简版的router
 */
import $ from 'zepto'
import { debounce } from 'lodash'

interface Route {
  path: string
  component: {
    /**
     * Others
     */
    [key: string]: unknown
    /**
     * 返回模板
     */
    template: (pageData: PAGE_CONFIG) => $
    /**
     * Called when the route is mounted.
     * Event binding should be done here.
     */
    mounted?: ($router: LiteRouter) => void
    /**
     * Called when the route is unmounted.
     * Event unbinding should be done here.
     */
    destroyed?: () => void
  }
}

function getUrl(path: string) {
  const href = window.location.href
  const i = href.indexOf('#')
  const base = i >= 0 ? href.slice(0, i) : href
  return `${base}#${path}`
}

function pushHash(path: string) {
  window.location.hash = path
}

function getHash(): string {
  // We can't use window.location.hash here because it's not
  // consistent across browsers - Firefox will pre-decode it!
  let href = window.location.href
  const index = href.indexOf('#')
  // empty path
  if (index < 0) return ''

  href = href.slice(index + 1)

  return href
}

function ensureSlash(): boolean {
  const path = getHash()
  if (path.charAt(0) === '/') {
    return true
  }
  replaceHash('/' + path)
  return false
}

function replaceHash(path: string) {
  window.location.replace(getUrl(path))
}

export class LiteRouter {
  private routes: Route[]
  private currentRoute: Route | null = null
  private routerView: $.Z<HTMLElement>
  constructor({
    routes,
    routerView,
  }: {
    routes: Route[]
    /**
     * Dom query selector
     */
    routerView: string
  }) {
    this.routes = routes
    const view = $(routerView)
    if (view.length === 0) {
      return
    }
    this.routerView = view
    ensureSlash()
    // 第一次进来不带hash，则会执行两次handleRoutingEvent，一次手动，一次hashchange
    this.handleRoutingEvent = debounce(
      this.handleRoutingEvent,
      10
    )
    window.addEventListener(
      'hashchange',
      this.handleRoutingEvent.bind(this)
    )
    this.handleRoutingEvent()
  }

  private handleRoutingEvent() {
    const route = this.match(getHash())
    // 命中路由，执行代码
    if (route !== false) {
      this.run(route)
    } else {
      replaceHash('/')
    }
  }

  private normalized(path: string) {
    let query = ''

    const queryIndex = path.indexOf('?')
    if (queryIndex >= 0) {
      query = path.slice(queryIndex + 1)
      path = path.slice(0, queryIndex)
    }

    return {
      path,
      query,
    }
  }

  private match(raw: string) {
    const normalized = this.normalized(raw)
    for (const route of this.routes) {
      if (route.path === normalized.path) {
        return route
      }
    }
    return false
  }

  private run(route: Route) {
    if (this.currentRoute) {
      // 路由相同不处理
      if (this.currentRoute.path === route.path) {
        return
      }
      // 卸载当前页面
      if (this.currentRoute.component.destroyed) {
        this.currentRoute.component.destroyed()
      }
    }

    this.nextTick(() => {
      if (!route.component.template) {
        return
      }
      this.routerView
        .empty()
        .append(
          route.component.template(
            (window as any).__INIT_STATE__
          )
        )
      this.nextTick(() => {
        if (route.component.mounted) {
          route.component.mounted(this)
        }
        pushHash(route.path)
        this.currentRoute = route
      })
    })
  }

  push(path: string) {
    const normalized = this.match(path)
    if (normalized === false) {
      return
    }
    if (
      this.currentRoute &&
      this.currentRoute.path === normalized.path
    ) {
      return
    }
    this.run(normalized)
  }

  private nextTick(cb: () => void) {
    return setTimeout(cb, 0)
  }
}
